import { getAccessToken, getStoredPolicies } from './authTokens';
import type { AuthTokensResponse, UserPolicy } from '../types/auth';

/**
 * Check if a single policy grants Lib Access / Lib Admin on E Lib.
 *
 * Example target policy:
 * {
 *   "userPolicyId": 18,
 *   "policyId": 365,
 *   "policyName": "Lib Access",
 *   "resource": "lib/*",
 *   "action": "lib:*",
 *   "effect": "allow",
 *   "applicationId": 4,
 *   "applicationName": "E Lib Dev",
 *   "startsAt": null,
 *   "expiresAt": null
 * }
 */
export function isLibAdminPolicy(policy: unknown): boolean {
  if (!policy || typeof policy !== 'object') {
    return false;
  }

  const p = policy as UserPolicy;

  // 1. Effect must be "allow" (case-insensitive)
  if (typeof p.effect === 'string' && p.effect.trim().toLowerCase() !== 'allow') {
    return false;
  }

  // 2. Time validity checks (if timestamps provided)
  const now = Date.now();
  if (p.startsAt) {
    const startTime = new Date(p.startsAt).getTime();
    if (Number.isFinite(startTime) && startTime > now) {
      return false; // Not yet valid
    }
  }
  if (p.expiresAt) {
    const expiryTime = new Date(p.expiresAt).getTime();
    if (Number.isFinite(expiryTime) && expiryTime <= now) {
      return false; // Expired
    }
  }

  const resource = typeof p.resource === 'string' ? p.resource.trim().toLowerCase() : '';
  const action = typeof p.action === 'string' ? p.action.trim().toLowerCase() : '';
  const policyName = typeof p.policyName === 'string' ? p.policyName.trim().toLowerCase() : '';
  const appName = typeof p.applicationName === 'string' ? p.applicationName.trim().toLowerCase() : '';
  const appId = typeof p.applicationId === 'number' ? p.applicationId : null;

  // 3. Application match: "E Lib Dev", "E Lib", "ELib", appId 4, or applicationName containing "lib"
  const isTargetApp =
    appId === 4 ||
    appName.includes('lib') ||
    appName === '' ||
    appName === 'e lib dev';

  // 4. Resource / Action / Policy match:
  // - resource matches "lib/*", "lib", starts with "lib/", or "*"
  // - action matches "lib:*", "lib", starts with "lib:", or "*"
  // - policyName matches "lib access", "lib admin", or contains "lib"
  const matchesResource =
    resource === 'lib/*' ||
    resource === 'lib' ||
    resource.startsWith('lib/') ||
    resource === '*';

  const matchesAction =
    action === 'lib:*' ||
    action === 'lib' ||
    action.startsWith('lib:') ||
    action === '*';

  const matchesPolicyName =
    policyName.includes('lib') ||
    policyName === 'lib access' ||
    policyName === 'lib admin';

  // Specific check: if resource and action specify lib access
  if (matchesResource && matchesAction) {
    return isTargetApp;
  }

  // Fallback check: if application is elib and policy grants access or has lib name
  if (isTargetApp && (matchesResource || matchesAction || matchesPolicyName)) {
    return true;
  }

  return false;
}

/**
 * Check whether a list of policies contains at least one active Lib Admin / Lib Access policy.
 */
export function hasLibAdminPolicy(policies: unknown[] | null | undefined): boolean {
  if (!policies || !Array.isArray(policies) || policies.length === 0) {
    return false;
  }
  return policies.some((policy) => isLibAdminPolicy(policy));
}

/**
 * Safely parse claims from a JWT string.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const encoded = part.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(encoded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Extract policies from auth login/refresh response, JWT payload, or stored policies.
 */
export function extractPoliciesFromAuthData(
  authData?: Partial<AuthTokensResponse> | Record<string, unknown> | null,
): unknown[] {
  if (!authData) return [];

  // 1. Direct response properties (e.g. activePolicies, policies)
  const candidateLists = [
    authData.activePolicies,
    authData.policies,
    (authData as Record<string, unknown>).userPolicies,
  ];

  for (const list of candidateLists) {
    if (Array.isArray(list) && list.length > 0) {
      return list;
    }
  }

  // 2. Decode from access_token if present in authData
  if (typeof authData.access_token === 'string') {
    const payload = decodeJwtPayload(authData.access_token);
    if (payload) {
      const nestedUser =
        payload.user && typeof payload.user === 'object'
          ? (payload.user as Record<string, unknown>)
          : null;

      const tokenLists = [
        payload.activePolicies,
        payload.policies,
        payload.userPolicies,
        nestedUser?.activePolicies,
        nestedUser?.policies,
        nestedUser?.userPolicies,
      ];

      for (const list of tokenLists) {
        if (Array.isArray(list) && list.length > 0) {
          return list;
        }
      }
    }
  }

  return [];
}

/**
 * Verify if the provided auth data (or currently active session) has Lib Admin access.
 */
export function checkUserLibAdminAccess(
  authData?: Partial<AuthTokensResponse> | Record<string, unknown> | null,
): boolean {
  if (authData) {
    const policies = extractPoliciesFromAuthData(authData);
    if (hasLibAdminPolicy(policies)) {
      return true;
    }

    // Also check if JWT payload directly has isAdmin flag
    if (typeof authData.access_token === 'string') {
      const payload = decodeJwtPayload(authData.access_token);
      if (payload && (payload.isAdmin === true || payload.is_admin === true)) {
        return true;
      }
    }

    return false;
  }

  return isCurrentUserLibAdmin();
}

/**
 * Check if the currently logged-in user in localStorage has active Lib Admin access.
 */
export function isCurrentUserLibAdmin(): boolean {
  const token = getAccessToken();
  if (!token) return false;

  // 1. Check stored policies in localStorage
  const storedPolicies = getStoredPolicies();
  if (hasLibAdminPolicy(storedPolicies)) {
    return true;
  }

  // 2. Check policies from JWT payload
  const payload = decodeJwtPayload(token);
  if (payload) {
    if (payload.isAdmin === true || payload.is_admin === true) {
      return true;
    }

    const nestedUser =
      payload.user && typeof payload.user === 'object'
        ? (payload.user as Record<string, unknown>)
        : null;

    const tokenPolicies = [
      payload.activePolicies,
      payload.policies,
      payload.userPolicies,
      nestedUser?.activePolicies,
      nestedUser?.policies,
      nestedUser?.userPolicies,
    ];

    for (const list of tokenPolicies) {
      if (Array.isArray(list) && hasLibAdminPolicy(list)) {
        return true;
      }
    }
  }

  return false;
}
